"use client";

import { CANT_FIND_OPTION_ID } from "@faire/retailer-visitor-shared/components/StoreSearchDropdown/StoreSearchDropdown";
import { SignupFormContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/CommonStyles";
import { useSignUpUPLT } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpUPLT";
import {
  getBusinnessTypeDetails,
  isOpeningSoon,
} from "@faire/retailer-visitor-shared/views/SignUp/_utils/signupUtilities";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSaveRequestAndGoToNextStep } from "@faire/retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import {
  StoreInfoData,
  StoreInfoForm,
  useSchema,
} from "@faire/retailer-visitor-shared/views/SignUp/store-info/__internal__/StoreInfoForm";
import { useSignUpPrefetchEffect } from "@faire/retailer-visitor-shared/views/SignUp/useSignUpPrefetchEffect";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IMatchedBusiness } from "@faire/web-api--source/indigofair/data/IMatchedBusiness";
import { Formik } from "formik";
import React from "react";

export const StoreInfoStep: React.FC = () => {
  const { saveAndGoToNextStep } = useSaveRequestAndGoToNextStep(
    Step.STORE_INFO
  );
  const [
    {
      business_platforms,
      legal_business_name,
      matched_business,
      website_url,
      reported_creation_time,
      country,
      postal_code,
    },
  ] = useSignUpRequest([
    "business_platforms",
    "legal_business_name",
    "matched_business",
    "website_url",
    "reported_creation_time",
    "country",
    "postal_code",
    "isOwFGrantFlow",
  ]);
  useSignUpUPLT({ step: Step.STORE_INFO });
  useSignUpPrefetchEffect(Step.STORE_INFO);

  const { hasOnline, requirePostalCode } =
    getBusinnessTypeDetails(business_platforms);

  const isPostalCodeOptional =
    isOpeningSoon(reported_creation_time) || !requirePostalCode;

  const schema = useSchema({
    isPostalCodeOptional,
    country,
    hasOnline,
  });

  const initialValues: StoreInfoData = {
    matched_business: matched_business ?? IMatchedBusiness.build({}),
    store_name: matched_business?.name || "",
    website_url: website_url || "",
    legal_business_name: legal_business_name || "",
    postal_code: postal_code || "",
  };

  const onNext = (values: StoreInfoData) => {
    const matchedBusiness = IMatchedBusiness.build(values.matched_business);

    if (!matchedBusiness.type) {
      matchedBusiness.id = CANT_FIND_OPTION_ID;
      matchedBusiness.type = IMatchedBusiness.Type.MANUAL;
      matchedBusiness.name = values.store_name;
    }

    values.matched_business = matchedBusiness;
    saveAndGoToNextStep(values);
  };

  return (
    <SignupFormContainer>
      <Typography variant="displaySSerifRegular">
        <StoreInfoTitle businessPlatforms={business_platforms} />
      </Typography>
      <Spacer height="4x" />
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={onNext}
      >
        <StoreInfoForm />
      </Formik>
    </SignupFormContainer>
  );
};

const StoreInfoTitle: React.FC<{ businessPlatforms: string[] }> = ({
  businessPlatforms,
}) => {
  const { hasOnline, hasPopup } = getBusinnessTypeDetails(businessPlatforms);

  if (hasPopup && businessPlatforms.length === 1) {
    return (
      <StrictLocalMsg
        defaultMessage="What’s your pop-up’s name?"
        description={{
          text: "Header for store info page asking for pop-up's name",
          img: "https://cdn.faire.com/fastly/ae1386d960b804a7184ea1ca69c92faa589855bed57de83aa950953c41bbc5d4.png",
        }}
      />
    );
  } else if (hasOnline) {
    return (
      <StrictLocalMsg
        defaultMessage="What’s your store’s name and website?"
        description={{
          text: "Header for store info page asking for store name and website",
          img: "https://cdn.faire.com/fastly/474c9893c33e5b41595acc392063a7703cf3607a2d839fb53300e0eabce7745d.png",
        }}
      />
    );
  }
  return (
    <StrictLocalMsg
      defaultMessage="What’s your store’s name?"
      description={{
        text: "Header for store info page asking for store name",
        img: "https://cdn.faire.com/fastly/671e5adc18216c20b9cc8edc75c7108e25bbd85372b1be2810270ee27aeaacf8.png",
      }}
    />
  );
};
